/**
 * Multi-language detector for PickStar platform
 * Supports: English (default), Brazilian Portuguese (pt-BR), Spanish (es)
 *
 * Detection priority:
 * 1. localStorage user preference
 * 2. URL parameter (?lang=es)
 * 3. Browser navigator.language
 * 4. Default to English
 */

export const SUPPORTED_LANGUAGES = {
  EN: 'en',
  ES: 'es',
  PT_BR: 'pt-br'
};

export const LANGUAGE_NAMES = {
  [SUPPORTED_LANGUAGES.EN]: 'English',
  [SUPPORTED_LANGUAGES.ES]: 'Español',
  [SUPPORTED_LANGUAGES.PT_BR]: 'Português'
};

/**
 * Detects the user's preferred language
 * @returns {string} Language code ('en', 'es', or 'pt-br')
 */
export const detectUserLanguage = () => {
  // Priority 1: Check URL params (useful for testing and direct links)
  if (typeof window !== 'undefined' && window.location) {
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get('lang')?.toLowerCase();

    if (urlLang === 'es' || urlLang === 'spanish') {
      return SUPPORTED_LANGUAGES.ES;
    }
    if (urlLang === 'pt-br' || urlLang === 'portuguese') {
      return SUPPORTED_LANGUAGES.PT_BR;
    }
    if (urlLang === 'en' || urlLang === 'english') {
      return SUPPORTED_LANGUAGES.EN;
    }
  }

  // Priority 2: Check localStorage for explicit user preference
  if (typeof localStorage !== 'undefined') {
    const userLang = localStorage.getItem('userLanguage');
    if (userLang && Object.values(SUPPORTED_LANGUAGES).includes(userLang)) {
      return userLang;
    }
  }

  // Priority 3: Check browser navigator.language
  if (typeof navigator !== 'undefined' && navigator.language) {
    const browserLang = navigator.language.toLowerCase();

    // Brazilian Portuguese - check for 'pt-br' or just 'pt' (Chrome sensors often use just 'pt')
    if (browserLang === 'pt-br' || browserLang.startsWith('pt-br') || browserLang === 'pt' || browserLang.startsWith('pt-')) {
      return SUPPORTED_LANGUAGES.PT_BR;
    }

    // Spanish (any variant)
    if (browserLang.startsWith('es')) {
      return SUPPORTED_LANGUAGES.ES;
    }
  }

  // Priority 4: Check navigator.languages array
  if (typeof navigator !== 'undefined' && navigator.languages) {
    for (const lang of navigator.languages) {
      const lowerLang = lang.toLowerCase();

      // Brazilian Portuguese - check for 'pt-br' or just 'pt'
      if (lowerLang === 'pt-br' || lowerLang.startsWith('pt-br') || lowerLang === 'pt' || lowerLang.startsWith('pt-')) {
        return SUPPORTED_LANGUAGES.PT_BR;
      }
      // Spanish (any variant)
      if (lowerLang.startsWith('es')) {
        return SUPPORTED_LANGUAGES.ES;
      }
    }
  }

  // Default: English
  return SUPPORTED_LANGUAGES.EN;
};

/**
 * Gets the user language without caching (always fresh)
 * Previously this was cached, but caching caused issues where users got "stuck"
 * in a language after changing settings. Reading from localStorage/navigator is
 * very fast (~0.1ms), so caching isn't necessary and caused more problems than it solved.
 *
 * @returns {string} Language code
 */
export const getUserLanguage = () => {
  // Always detect fresh - no caching
  // This fixes the "stuck in Spanish" bug where cached values persisted
  return detectUserLanguage();
};

/**
 * Force refresh the language cache (kept for backwards compatibility)
 * Note: Since we removed caching, this just returns getUserLanguage()
 * @returns {string} The newly detected language
 */
export const refreshLanguage = () => {
  // No cache to clear anymore, but kept for backwards compatibility
  return getUserLanguage();
};

/**
 * Set user language preference
 * @param {string} lang - Language code ('en', 'es', or 'pt-br')
 */
export const setUserLanguage = (lang) => {
  if (!Object.values(SUPPORTED_LANGUAGES).includes(lang)) {
    console.warn(`[Language Detection] ❌ Invalid language: ${lang}. Must be one of: ${Object.values(SUPPORTED_LANGUAGES).join(', ')}`);
    return;
  }

  if (typeof localStorage !== 'undefined') {
    const oldLang = localStorage.getItem('userLanguage');
    localStorage.setItem('userLanguage', lang);
    // Only log when user explicitly changes language
    console.log(`[Language Detection] Language changed: ${oldLang || 'auto'} → ${lang}`);
    refreshLanguage();
  }
};

/**
 * Check if user is Brazilian (backwards compatibility)
 * @returns {boolean}
 */
export const isBrazilianUser = () => {
  return getUserLanguage() === SUPPORTED_LANGUAGES.PT_BR;
};

/**
 * Check if user is Spanish-speaking
 * @returns {boolean}
 */
export const isSpanishUser = () => {
  return getUserLanguage() === SUPPORTED_LANGUAGES.ES;
};

/**
 * Backwards compatibility exports
 */
export const getBrazilianStatus = isBrazilianUser;

/**
 * Translation helper factory
 * Creates a t() function for a given set of language mappings
 *
 * @param {Object} mappings - Object with language codes as keys and translation objects as values
 * @returns {Function} Translation function
 *
 * @example
 * import { createTranslator } from '../utils/detectLanguage';
 * import { homepagePtBR } from './mappings/homepage.pt-br';
 * import { homepageES } from './mappings/homepage.es';
 *
 * const t = createTranslator({
 *   'pt-br': homepagePtBR,
 *   'es': homepageES
 * });
 *
 * // Usage in JSX
 * <h1>{t("Welcome to PickStar")}</h1>
 */
export const createTranslator = (mappings) => {
  return (text) => {
    const lang = getUserLanguage();

    // If English, return text as-is
    if (lang === SUPPORTED_LANGUAGES.EN) {
      return text;
    }

    // Try to find translation in mappings
    if (mappings[lang] && mappings[lang][text]) {
      return mappings[lang][text];
    }

    // Fallback to original text
    return text;
  };
};

// ============================================================================
// CURRENCY DETECTION & UTILITIES
// ============================================================================

/**
 * Supported currencies for Star Coins purchases
 */
export const SUPPORTED_CURRENCIES = {
  USD: 'usd',
  BRL: 'brl',
  MXN: 'mxn',
  ARS: 'ars',
  COP: 'cop',
  CLP: 'clp'
};

/**
 * Currency metadata including symbols, names, and exchange rates
 * Rates are used as fallbacks when custom prices aren't set in Firestore
 */
export const CURRENCY_INFO = {
  usd: {
    symbol: '$',
    name: 'US Dollar',
    rate: 1,
    flag: '🇺🇸'
  },
  brl: {
    symbol: 'R$',
    name: 'Brazilian Real',
    rate: 5.5,
    flag: '🇧🇷'
  },
  mxn: {
    symbol: '$',
    name: 'Mexican Peso',
    rate: 20,
    flag: '🇲🇽'
  },
  ars: {
    symbol: '$',
    name: 'Argentine Peso',
    rate: 1030,
    flag: '🇦🇷'
  },
  cop: {
    symbol: '$',
    name: 'Colombian Peso',
    rate: 3800,
    flag: '🇨🇴'
  },
  clp: {
    symbol: '$',
    name: 'Chilean Peso',
    rate: 950,
    flag: '🇨🇱'
  }
};

/**
 * Detects the user's preferred currency based on locale
 * Detection priority:
 * 1. localStorage user preference
 * 2. URL parameter (?currency=mxn)
 * 3. Browser navigator.language (country code)
 * 4. Default to USD
 *
 * @returns {string} Currency code ('usd', 'brl', 'mxn', 'ars', 'cop', 'clp')
 */
export const detectUserCurrency = () => {
  // console.log('[Currency Detection] Starting...');
  // console.log('[Currency Detection] All localStorage keys:', Object.keys(localStorage));

  // Priority 1: Check localStorage for explicit user preference
  if (typeof localStorage !== 'undefined') {
    const savedCurrency = localStorage.getItem('userCurrency');
    // console.log('[Currency Detection] localStorage.userCurrency:', savedCurrency);
    if (savedCurrency && Object.values(SUPPORTED_CURRENCIES).includes(savedCurrency.toLowerCase())) {
      // console.log('[Currency Detection] ✅ Using localStorage:', savedCurrency);
      return savedCurrency.toLowerCase();
    } else if (savedCurrency) {
      // console.log('[Currency Detection] ⚠️ Invalid localStorage currency, ignoring:', savedCurrency);
    }
  }

  // Priority 2: Check URL params (useful for testing and direct links)
  if (typeof window !== 'undefined' && window.location) {
    const params = new URLSearchParams(window.location.search);
    const urlCurrency = params.get('currency')?.toLowerCase();
    // console.log('[Currency Detection] URL param:', urlCurrency);

    if (urlCurrency && Object.values(SUPPORTED_CURRENCIES).includes(urlCurrency)) {
      // console.log('[Currency Detection] ✅ Using URL param:', urlCurrency);
      return urlCurrency;
    }
  }

  // Priority 3: Check browser navigator.language for country code
  if (typeof navigator !== 'undefined' && navigator.language) {
    const locale = navigator.language.toLowerCase();
    // console.log('[Currency Detection] navigator.language:', navigator.language, '→', locale);

    // Map locales to currencies
    if (locale === 'pt-br' || locale.startsWith('pt-br')) {
      // console.log('[Currency Detection] ✅ Detected BRL');
      return 'brl';
    }
    if (locale === 'es-mx' || locale.startsWith('es-mx')) {
      // console.log('[Currency Detection] ✅ Detected MXN');
      return 'mxn';
    }
    if (locale === 'es-ar' || locale.startsWith('es-ar')) {
      // console.log('[Currency Detection] ✅ Detected ARS');
      return 'ars';
    }
    if (locale === 'es-cl' || locale.startsWith('es-cl')) {
      // console.log('[Currency Detection] ✅ Detected CLP');
      return 'clp';
    }
    if (locale === 'es-co' || locale.startsWith('es-co')) {
      // console.log('[Currency Detection] ✅ Detected COP');
      return 'cop';
    }
  }

  // Priority 4: Check navigator.languages array
  if (typeof navigator !== 'undefined' && navigator.languages) {
    // console.log('[Currency Detection] navigator.languages:', navigator.languages);
    for (const lang of navigator.languages) {
      const locale = lang.toLowerCase();

      if (locale === 'pt-br' || locale.startsWith('pt-br')) {
        // console.log('[Currency Detection] ✅ Detected BRL from languages array');
        return 'brl';
      }
      if (locale === 'es-mx' || locale.startsWith('es-mx')) {
        // console.log('[Currency Detection] ✅ Detected MXN from languages array');
        return 'mxn';
      }
      if (locale === 'es-ar' || locale.startsWith('es-ar')) {
        // console.log('[Currency Detection] ✅ Detected ARS from languages array');
        return 'ars';
      }
      if (locale === 'es-cl' || locale.startsWith('es-cl')) {
        // console.log('[Currency Detection] ✅ Detected CLP from languages array');
        return 'clp';
      }
      if (locale === 'es-co' || locale.startsWith('es-co')) {
        // console.log('[Currency Detection] ✅ Detected COP from languages array');
        return 'cop';
      }
    }
  }

  // Default: USD
  // console.log('[Currency Detection] ⚠️ Defaulting to USD');
  return 'usd';
};

// Cache the currency result for performance
let cachedCurrency = null;

/**
 * Gets the cached user currency (faster than re-detecting)
 * @returns {string} Currency code
 */
export const getUserCurrency = () => {
  if (cachedCurrency === null) {
    cachedCurrency = detectUserCurrency();
  }
  return cachedCurrency;
};

/**
 * Force refresh the currency cache (call after user changes currency)
 * @returns {string} The newly detected currency
 */
export const refreshCurrency = () => {
  cachedCurrency = null;
  return getUserCurrency();
};

/**
 * Set user currency preference
 * @param {string} currency - Currency code ('usd', 'brl', 'mxn', etc.)
 */
export const setUserCurrency = (currency) => {
  const currencyLower = currency.toLowerCase();
  if (!Object.values(SUPPORTED_CURRENCIES).includes(currencyLower)) {
    console.warn(`Invalid currency: ${currency}. Must be one of: ${Object.values(SUPPORTED_CURRENCIES).join(', ')}`);
    return;
  }

  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('userCurrency', currencyLower);
    refreshCurrency();
  }
};

/**
 * Calculate price for a package in a specific currency
 * Uses custom Firestore price if available, otherwise falls back to rate calculation
 *
 * @param {Object} packageData - Package data from Firestore
 * @param {string} currency - Currency code
 * @returns {number} Price in the specified currency
 */
export const calculatePrice = (packageData, currency) => {
  if (!packageData || !currency) {
    console.warn('calculatePrice: Missing packageData or currency');
    return 0;
  }

  // USD is always the base price
  if (currency === 'usd') {
    return packageData.price;
  }

  // Try to get custom price from Firestore (e.g., 'mxnPrice', 'brlPrice')
  const customPriceField = `${currency}Price`;
  const customPrice = packageData[customPriceField];

  // If custom price exists in Firestore, use it
  if (customPrice !== undefined && customPrice !== null) {
    return customPrice;
  }

  // Otherwise, calculate using exchange rate
  const currencyInfo = CURRENCY_INFO[currency];
  if (!currencyInfo) {
    console.warn(`calculatePrice: Unknown currency ${currency}, falling back to USD`);
    return packageData.price;
  }

  // Return calculated price
  return Math.round(packageData.price * currencyInfo.rate);
};
